import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Selector from 'components/shared/selector/Selector';
import { rfasApi } from 'services/api/rfas-api';

// Statuses an open item must not be linked to (drafts and discarded RFAs).
const EXCLUDED_STATUSES = [
  'draft',
  'draft_pending',
  'draft_for_record',
  'discarded',
];
// Safety cap on pagination so a huge log can never loop forever.
const MAX_PAGES = 40;

export const rfaOptionLabel = (rfa) =>
  `RFA #${rfa.number != null ? rfa.number : rfa.formatted_number || rfa.id}${
    rfa.request_title ? ` ${rfa.request_title}` : ''
  }`;

export const rfaOptionsFrom = (rfas) =>
  (rfas || [])
    .filter((rfa) => !EXCLUDED_STATUSES.includes(rfa.status))
    .map((rfa) => ({ value: rfa.id, label: rfaOptionLabel(rfa) }));

const LOG_FILTERS = {
  // `null`, not false: only null skips the pre/not_pre scope (see
  // OpenItemRfaPickerModal). `active` = the server's not-draft, not-discarded scope.
  pre: null,
  active: true,
  due: 'all',
  status: 'all',
  search: '',
  number_order: 'all',
  company_number_order: 'all',
  title_order: 'all',
  submitted_order: 'all',
  reviewed_order: 'all',
  resolved_order: 'all',
};

// Optional "RFA" picker for the open item form. Lists ONLY this project's
// non-draft RFAs, through the existing project RFA log endpoint.
const OpenItemRfaSelect = ({ projectId, value, onChange }) => {
  const [rfas, setRfas] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      let all = [];
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const response = await rfasApi.getProjectRfas(projectId, {
          ...LOG_FILTERS,
          page,
        });
        if (!response.ok) break;
        all = all.concat(response.rfas || []);
        if (all.length >= (response.total || 0) || !response.rfas.length) break;
      }
      if (!cancelled) setRfas(all);
    };
    if (projectId) loadAll();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <Selector
      placeholder="RFA (optional)"
      clearable
      options={rfaOptionsFrom(rfas)}
      value={value || null}
      onChange={(option) => onChange(option ? option.value : null)}
    />
  );
};

OpenItemRfaSelect.propTypes = {
  projectId: PropTypes.number,
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

export default OpenItemRfaSelect;
